/**
 * SignIn: email + password. Shows a "check your email" nudge after
 * registration, and a verification reminder when the account is unverified,
 * both with a "resend verification email" action.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { resendVerification } from '../lib/auth';
import { useAuthStore } from '../stores/auth';
import { AuthField, AuthShell, AuthSubmit } from '../components/AuthShell';

function errorMessage(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.code === 'email_not_verified')
      return t('auth.notVerified');
    if (error.code === 'account_locked')
      return t('auth.accountLocked');
    if (error.code === 'rate_limited') return t('auth.rateLimited');
    return error.message;
  }
  return error instanceof Error ? error.message : t('auth.signInFailed');
}

export function SignIn(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const registered = (location.state as { registered?: boolean } | null)?.registered === true;
  const registeredEmail = (location.state as { email?: string } | null)?.email ?? '';
  const [email, setEmail] = useState(registeredEmail);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [resend, setResend] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle');

  async function handleResend(): Promise<void> {
    if (!email.trim()) return;
    setResend('busy');
    try {
      await resendVerification(email.trim());
      setResend('sent');
    } catch {
      setResend('failed');
    }
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setUnverified(false);
    try {
      await login(email.trim(), password);
      navigate('/map');
    } catch (err) {
      setError(errorMessage(err, t));
      setUnverified(err instanceof ApiError && err.code === 'email_not_verified');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={t('auth.signInTitle')} subtitle={t('auth.signInSubtitle')}>
      {registered && (
        <p role="status" className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {t('auth.accountCreated')}
        </p>
      )}
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <AuthField
          label={t('auth.email')}
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <AuthField
          label={t('auth.password')}
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        {(registered || unverified) && (
          <div className="text-sm">
            {resend === 'sent' ? (
              <p role="status" className="text-stone-600">
                {t('auth.verificationResent')}
              </p>
            ) : (
              <>
                <button
                  type="button"
                  className="font-medium text-tenant-primary hover:underline disabled:opacity-60"
                  disabled={resend === 'busy' || !email.trim()}
                  onClick={() => void handleResend()}
                >
                  {resend === 'busy' ? t('auth.resending') : t('auth.resendVerification')}
                </button>
                {resend === 'failed' && (
                  <p role="alert" className="mt-1 text-red-600">
                    {t('auth.resendFailed')}
                  </p>
                )}
              </>
            )}
          </div>
        )}
        <AuthSubmit busy={busy} busyLabel={t('auth.signingIn')}>
          {t('auth.signInButton')}
        </AuthSubmit>
      </form>
      <div className="mt-4 flex items-center justify-between text-sm">
        <p className="text-stone-600">
          {t('auth.newHere')}{' '}
          <Link to="/signup" className="font-medium text-tenant-primary hover:underline">
            {t('auth.createFree')}
          </Link>
        </p>
        <Link to="/forgot-password" className="text-stone-500 hover:underline">
          {t('auth.forgotPassword')}
        </Link>
      </div>
    </AuthShell>
  );
}
